/**
 * M11b-4 — Client wrappers for the advisor e-mail/token invite Cloud Functions.
 *
 * Thin httpsCallable bindings (same pattern as {@link ./stripeApi}). All hard
 * security checks live server-side (architecture-m11b §3); these helpers only marshal
 * input/output and normalise errors for the UI.
 *
 * INERT-Disziplin: the FE flag {@link ADVISOR_EMAIL_INVITE_ENABLED} only governs UI
 * VISIBILITY. Even with the flag on, the Cloud Functions remain fail-closed behind
 * their own server-side `ADVISOR_EMAIL_INVITE_ENABLED` env flag (Default off) and
 * throw `failed-precondition` until it is set on Live.
 */

import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebaseConfig';

/**
 * FE feature flag (CRA env, Default off). Read once at module load. `=== 'true'`
 * fail-closed: any other value (undefined, '', 'false') keeps the e-mail invite UI
 * hidden and the M11b-2 „in Vorbereitung"-Platzhalter in place.
 */
export const ADVISOR_EMAIL_INVITE_ENABLED =
  process.env.REACT_APP_ADVISOR_EMAIL_INVITE_ENABLED === 'true';

/** Uniform server response for the creation callable (Anti-Enumeration, A5). */
export interface InviteAdvisorResult {
  status: 'invited';
}

/**
 * Server response for the acceptance callable (M11b Portal-Fix, Bug 3).
 * `tenantId` = the mandant's scopeUid, used by the panel to auto-`switchTenant`
 * into the newly accepted tenant; `mandantName` is the resolved display name for
 * the success UX (null when not resolvable → generic fallback copy).
 */
export interface AcceptInviteResult {
  status: 'accepted';
  tenantId: string;
  mandantName: string | null;
}

/** Server response for the read-only preview callable (M11b Portal-Fix, Bug 2). */
export interface PreviewAdvisorInviteResult {
  mandantName: string | null;
}

/**
 * Invites an advisor by e-mail. The server resolves registered vs non-registered
 * and ALWAYS returns `{status:'invited'}` (uniform — the UI must not branch on it).
 *
 * @param email          invitee e-mail (server normalises lowercase+trim, A9).
 * @param ownerConfirmed the owner's mandatory confirmation checkbox value (J-a).
 *                       The server rejects anything but `true` with failed-precondition.
 * @param consentAccepted the §3-Mandanten-Consent (B3). Optional/additiv: der Server
 *                       schreibt die Consent-Marker, sobald `true` gesendet wird
 *                       (auch bei Enforcement-Flag off — füllt den Bestand vor dem
 *                       Flip). Ist `KANZLEI_CONSENT_ENFORCED` scharf, verlangt der
 *                       Server `true` (sonst `failed-precondition 'consent_required'`).
 * @throws Error with a human-readable message on any server-side rejection.
 */
export async function inviteAdvisorByEmail(
  email: string,
  ownerConfirmed: boolean,
  consentAccepted?: boolean
): Promise<InviteAdvisorResult> {
  const callable = httpsCallable<
    { email: string; ownerConfirmed: boolean; consentAccepted?: boolean },
    InviteAdvisorResult
  >(functions, 'inviteAdvisorByEmail');
  try {
    const res = await callable({ email, ownerConfirmed, consentAccepted });
    return res.data;
  } catch (err) {
    throw normalizeCallableError(err);
  }
}

/**
 * Accepts a token invitation. Requires the caller to be email-verified server-side
 * (A1); if not, the call rejects with `failed-precondition` and the invite stays
 * pending — the caller can retry after verifying (re-invokable, A2).
 *
 * @param token the plaintext token taken from the `?invite=` URL parameter.
 * @throws Error with a human-readable message on any server-side rejection.
 */
export async function acceptInviteByToken(token: string): Promise<AcceptInviteResult> {
  const callable = httpsCallable<{ token: string }, AcceptInviteResult>(
    functions,
    'acceptInviteByToken'
  );
  try {
    const res = await callable({ token });
    return res.data;
  } catch (err) {
    throw normalizeCallableError(err);
  }
}

/**
 * Resolves ONLY the inviting company's display name for a token, so the Accept-UI
 * can show which Mandant sent the invite BEFORE any action (M11b Portal-Fix, Bug 2).
 *
 * Read-only server-side (writes/consumes nothing). Returns `{ mandantName: null }`
 * for any non-resolvable case (unknown/expired/consumed token — Anti-Enum, Decision A)
 * as well as when the flag is off (`failed-precondition` is caught and converted to a
 * null name so the panel stays silent, analogous to {@link listMyAdvisorInvites}).
 *
 * @param token the plaintext token taken from the `?invite=` URL parameter.
 */
export async function previewAdvisorInvite(
  token: string
): Promise<PreviewAdvisorInviteResult> {
  const callable = httpsCallable<{ token: string }, PreviewAdvisorInviteResult>(
    functions,
    'previewAdvisorInvite'
  );
  try {
    const res = await callable({ token });
    return res.data;
  } catch (err) {
    const normalized = normalizeCallableError(err);
    // Flag-off → resolve silently with no name so the panel shows the generic copy.
    if (normalized.code === 'failed-precondition') {
      return { mandantName: null };
    }
    throw normalized;
  }
}

/**
 * A single sent-advisor-invite entry as returned by the server (M11b-5).
 * Shape mirrors `SentAdvisorInviteEntry` in `tenantInviteFunctions.ts`.
 */
export interface SentAdvisorInvite {
  /** Invitee e-mail address (lowercase). Null for direct invites where auth lookup failed. */
  email: string | null;
  /** Current status of the invitation. */
  status: 'pending' | 'accepted' | 'expired' | 'declined';
  /**
   * `email` = token-based invite (non-registered invitee).
   * `registered` = direct invite to an already-registered user.
   */
  kind: 'email' | 'registered';
  /** Server-side creation timestamp (ms since epoch). */
  invitedAtMs: number | null;
  /** Expiry timestamp (ms), present for token invites only. */
  expiresAtMs?: number | null;
}

/** Response envelope from the `listMyAdvisorInvites` callable. */
export interface ListMyAdvisorInvitesResult {
  invites: SentAdvisorInvite[];
}

/**
 * Fetches the caller's own sent advisor invitations (owner-only view, M11b-5).
 *
 * Returns an empty list when the flag is off (the server throws `failed-precondition`
 * in that case, which is caught and converted to an empty result so the panel stays
 * inert with no error shown to the user — consistent with `return null` on the FE side).
 *
 * @throws Error with a human-readable message on unexpected server errors.
 */
export async function listMyAdvisorInvites(): Promise<ListMyAdvisorInvitesResult> {
  const callable = httpsCallable<Record<string, never>, ListMyAdvisorInvitesResult>(
    functions,
    'listMyAdvisorInvites'
  );
  try {
    const res = await callable({});
    return res.data;
  } catch (err) {
    const normalized = normalizeCallableError(err);
    // Flag-off → fail silently so the panel renders nothing rather than an error.
    if (normalized.code === 'failed-precondition') {
      return { invites: [] };
    }
    throw normalized;
  }
}

/**
 * Maps a Firebase callable error to a plain Error. Keeps the server-provided message
 * and exposes the function error code (e.g. `failed-precondition`) so callers can do
 * coarse branching (e.g. „not yet verified") without leaking internals.
 */
function normalizeCallableError(err: unknown): Error & { code?: string } {
  const anyErr = err as { code?: string; message?: string };
  const message =
    typeof anyErr?.message === 'string' && anyErr.message.length > 0
      ? anyErr.message
      : 'Request failed.';
  const out = new Error(message) as Error & { code?: string };
  // firebase callable errors carry codes like 'functions/failed-precondition'.
  if (typeof anyErr?.code === 'string') {
    out.code = anyErr.code.replace(/^functions\//, '');
  }
  return out;
}
